import React, { useState } from 'react';
import { SearchIcon, FilterIcon, DownloadIcon, CheckIcon, XIcon, AlertCircleIcon } from 'lucide-react';
import { formatCurrency } from '../../utils/currencyFormatter';
export function DiscrepancyReview({
  reconciliation,
  statements = {
    statements: []
  },
  locale = 'en-US'
}) {
  const [filterType, setFilterType] = useState('all'); // all, unmatched, partial, overpayment, underpayment
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedItems, setSelectedItems] = useState([]);
  // Process statements to identify discrepancies
  const processedStatements = statements.statements || [];
  const getDiscrepancyType = statement => {
    if (!statement.status || statement.status === 'Unmatched') return 'unmatched';
    if (statement.status === 'Ignored') return 'ignored';
    if (statement.status === 'Matched') return 'matched';
    if (statement.status === 'Partial Match') {
      if (statement.discrepancyAmount > 0) return 'overpayment';
      return 'underpayment';
    }
    return 'other';
  };
  const filteredStatements = processedStatements.filter(statement => {
    // Filter by type
    if (filterType !== 'all') {
      const discrepancyType = getDiscrepancyType(statement);
      if (filterType !== discrepancyType) return false;
    }
    // Search filter
    return statement.invoiceNumber.toLowerCase().includes(searchTerm.toLowerCase()) || statement.description && statement.description.toLowerCase().includes(searchTerm.toLowerCase());
  });
  const handleSelectItem = statement => {
    if (selectedItems.find(item => item.id === statement.id)) {
      setSelectedItems(selectedItems.filter(item => item.id !== statement.id));
    } else {
      setSelectedItems([...selectedItems, statement]);
    }
  };
  const handleSelectAll = () => {
    if (selectedItems.length === filteredStatements.length) {
      setSelectedItems([]);
    } else {
      setSelectedItems([...filteredStatements]);
    }
  };
  const handleBulkMarkAsMatched = () => {
    // In a real app, you would update the status in your database
    alert(`Marked ${selectedItems.length} items as matched`);
    setSelectedItems([]);
  };
  const handleBulkIgnore = () => {
    // In a real app, you would update the status in your database
    alert(`Ignored ${selectedItems.length} items`);
    setSelectedItems([]);
  };
  const handleExportDiscrepancies = () => {
    // In a real app, you would generate a CSV or PDF report
    alert('Exporting discrepancy report');
  };
  const getStatusColor = status => {
    switch (status) {
      case 'Matched':
        return 'bg-green-100 text-green-800';
      case 'Partial Match':
        return 'bg-yellow-100 text-yellow-800';
      case 'Ignored':
        return 'bg-gray-100 text-gray-800';
      default:
        return 'bg-red-100 text-red-800';
    }
  };
  const getDiscrepancyColor = type => {
    switch (type) {
      case 'matched':
        return 'text-green-600';
      case 'overpayment':
        return 'text-red-600';
      case 'underpayment':
        return 'text-yellow-600';
      case 'unmatched':
        return 'text-red-600';
      case 'ignored':
        return 'text-gray-600';
      default:
        return 'text-gray-600';
    }
  };
  // Count by discrepancy type
  const discrepancyCounts = {
    unmatched: processedStatements.filter(s => getDiscrepancyType(s) === 'unmatched').length,
    partial: processedStatements.filter(s => s.status === 'Partial Match').length,
    overpayment: processedStatements.filter(s => getDiscrepancyType(s) === 'overpayment').length,
    underpayment: processedStatements.filter(s => getDiscrepancyType(s) === 'underpayment').length
  };
  return <div data-id="element-1456">
      <div className="mb-6" data-id="element-1457">
        <h3 className="text-lg font-medium text-gray-900" data-id="element-1458">
          Discrepancy Review: {reconciliation.vendor}
        </h3>
        <p className="mt-1 text-sm text-gray-500" data-id="element-1459">
          Review and resolve discrepancies between vendor statements and AP
          invoices
        </p>
      </div>
      {/* Summary Stats */}
      <div className="mb-6 grid grid-cols-1 gap-5 sm:grid-cols-4" data-id="element-1460">
        <div className="bg-white overflow-hidden shadow rounded-lg" data-id="element-1461">
          <div className="px-4 py-5 sm:p-6" data-id="element-1462">
            <dt className="text-sm font-medium text-gray-500 truncate" data-id="element-1463">
              Unmatched
            </dt>
            <dd className="mt-1 text-3xl font-semibold text-red-600" data-id="element-1464">
              {discrepancyCounts.unmatched}
            </dd>
          </div>
        </div>
        <div className="bg-white overflow-hidden shadow rounded-lg" data-id="element-1465">
          <div className="px-4 py-5 sm:p-6" data-id="element-1466">
            <dt className="text-sm font-medium text-gray-500 truncate" data-id="element-1467">
              Partial Matches
            </dt>
            <dd className="mt-1 text-3xl font-semibold text-yellow-600" data-id="element-1468">
              {discrepancyCounts.partial}
            </dd>
          </div>
        </div>
        <div className="bg-white overflow-hidden shadow rounded-lg" data-id="element-1469">
          <div className="px-4 py-5 sm:p-6" data-id="element-1470">
            <dt className="text-sm font-medium text-gray-500 truncate" data-id="element-1471">
              Overpayments
            </dt>
            <dd className="mt-1 text-3xl font-semibold text-red-600" data-id="element-1472">
              {discrepancyCounts.overpayment}
            </dd>
          </div>
        </div>
        <div className="bg-white overflow-hidden shadow rounded-lg" data-id="element-1473">
          <div className="px-4 py-5 sm:p-6" data-id="element-1474">
            <dt className="text-sm font-medium text-gray-500 truncate" data-id="element-1475">
              Underpayments
            </dt>
            <dd className="mt-1 text-3xl font-semibold text-yellow-600" data-id="element-1476">
              {discrepancyCounts.underpayment}
            </dd>
          </div>
        </div>
      </div>
      {/* Filters and Search */}
      <div className="mb-6 bg-white p-4 shadow rounded-lg" data-id="element-1477">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4" data-id="element-1478">
          <div className="md:col-span-3" data-id="element-1479">
            <label htmlFor="search" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1480">
              Search
            </label>
            <div className="relative rounded-md shadow-sm" data-id="element-1481">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-1482">
                <SearchIcon className="h-5 w-5 text-gray-400" data-id="element-1483" />
              </div>
              <input type="text" name="search" id="search" className="focus:ring-indigo-500 focus:border-indigo-500 block w-full pl-10 sm:text-sm border-gray-300 rounded-md p-2 border" placeholder="Search by invoice number or description" value={searchTerm} onChange={e => setSearchTerm(e.target.value)} data-id="element-1484" />
            </div>
          </div>
          <div data-id="element-1485">
            <label htmlFor="discrepancy-filter" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1486">
              Discrepancy Type
            </label>
            <select id="discrepancy-filter" name="discrepancy-filter" className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md" value={filterType} onChange={e => setFilterType(e.target.value)} data-id="element-1487">
              <option value="all" data-id="element-1488">All Discrepancies</option>
              <option value="unmatched" data-id="element-1489">Unmatched</option>
              <option value="overpayment" data-id="element-1490">Overpayments</option>
              <option value="underpayment" data-id="element-1491">Underpayments</option>
              <option value="ignored" data-id="element-1492">Ignored</option>
            </select>
          </div>
        </div>
      </div>
      {/* Bulk Actions */}
      <div className="mb-4 flex justify-between items-center" data-id="element-1493">
        <div className="flex items-center" data-id="element-1494">
          <input id="select-all" name="select-all" type="checkbox" className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded" checked={selectedItems.length === filteredStatements.length && filteredStatements.length > 0} onChange={handleSelectAll} data-id="element-1495" />
          <label htmlFor="select-all" className="ml-2 text-sm text-gray-700" data-id="element-1496">
            Select All
          </label>
          <span className="ml-2 text-sm text-gray-500" data-id="element-1497">
            ({selectedItems.length} selected)
          </span>
        </div>
        <div className="flex space-x-3" data-id="element-1498">
          <button onClick={handleBulkMarkAsMatched} disabled={selectedItems.length === 0} className={`inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md shadow-sm text-white ${selectedItems.length > 0 ? 'bg-green-600 hover:bg-green-700' : 'bg-gray-300 cursor-not-allowed'} focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500`} data-id="element-1499">
            <CheckIcon className="h-4 w-4 mr-1" data-id="element-1500" />
            Mark as Matched
          </button>
          <button onClick={handleBulkIgnore} disabled={selectedItems.length === 0} className={`inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md shadow-sm text-white ${selectedItems.length > 0 ? 'bg-gray-600 hover:bg-gray-700' : 'bg-gray-300 cursor-not-allowed'} focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500`} data-id="element-1501">
            <XIcon className="h-4 w-4 mr-1" data-id="element-1502" />
            Ignore
          </button>
          <button onClick={handleExportDiscrepancies} className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-1503">
            <DownloadIcon className="h-4 w-4 mr-1" data-id="element-1504" />
            Export
          </button>
        </div>
      </div>
      {/* Discrepancies Table */}
      <div className="bg-white shadow overflow-hidden sm:rounded-lg" data-id="element-1505">
        <div className="overflow-x-auto" data-id="element-1506">
          <table className="min-w-full divide-y divide-gray-200" data-id="element-1507">
            <thead className="bg-gray-50" data-id="element-1508">
              <tr data-id="element-1509">
                <th scope="col" className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1510">
                  <span className="sr-only" data-id="element-1511">Select</span>
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1512">
                  Invoice #
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1513">
                  Date
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1514">
                  Statement Amount
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1515">
                  AP Amount
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1516">
                  Difference
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1517">
                  Status
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1518">
                  Discrepancy Type
                </th>
                <th scope="col" className="relative px-6 py-3" data-id="element-1519">
                  <span className="sr-only" data-id="element-1520">Actions</span>
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200" data-id="element-1521">
              {filteredStatements.length > 0 ? filteredStatements.map(statement => {
              const discrepancyType = getDiscrepancyType(statement);
              const isSelected = selectedItems.some(item => item.id === statement.id);
              return <tr key={statement.id} className={`hover:bg-gray-50 ${isSelected ? 'bg-indigo-50' : ''}`} data-id="element-1522">
                      <td className="px-3 py-4 whitespace-nowrap text-sm font-medium" data-id="element-1523">
                        <input type="checkbox" className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded" checked={isSelected} onChange={() => handleSelectItem(statement)} data-id="element-1524" />
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900" data-id="element-1525">
                        {statement.invoiceNumber}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-1526">
                        {statement.invoiceDate}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-1527">
                        {formatCurrency(statement.amount, 'USD', locale)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-1528">
                        {statement.matchedInvoiceId ? formatCurrency(statement.amount - (statement.discrepancyAmount || 0), 'USD', locale) : '-'}
                      </td>
                      <td className={`px-6 py-4 whitespace-nowrap text-sm ${statement.discrepancyAmount > 0 ? 'text-red-600' : statement.discrepancyAmount < 0 ? 'text-yellow-600' : 'text-gray-500'}`} data-id="element-1529">
                        {statement.discrepancyAmount ? formatCurrency(statement.discrepancyAmount, 'USD', locale) : '-'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap" data-id="element-1530">
                        <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusColor(statement.status)}`} data-id="element-1531">
                          {statement.status || 'Unmatched'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap" data-id="element-1532">
                        <span className={`inline-flex text-sm ${getDiscrepancyColor(discrepancyType)}`} data-id="element-1533">
                          {discrepancyType === 'overpayment' && 'Overpayment'}
                          {discrepancyType === 'underpayment' && 'Underpayment'}
                          {discrepancyType === 'unmatched' && 'Unmatched'}
                          {discrepancyType === 'matched' && 'Matched'}
                          {discrepancyType === 'ignored' && 'Ignored'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium" data-id="element-1534">
                        <button className="text-indigo-600 hover:text-indigo-900 mr-3" data-id="element-1535">
                          Details
                        </button>
                      </td>
                    </tr>;
            }) : <tr data-id="element-1536">
                  <td colSpan={9} className="px-6 py-4 text-center text-sm text-gray-500" data-id="element-1537">
                    No discrepancies found matching your criteria
                  </td>
                </tr>}
            </tbody>
          </table>
        </div>
      </div>
      {/* Audit Trail */}
      <div className="mt-8" data-id="element-1538">
        <h4 className="text-base font-medium text-gray-900 mb-4" data-id="element-1539">
          Audit Trail
        </h4>
        <div className="bg-white shadow overflow-hidden sm:rounded-lg" data-id="element-1540">
          <ul className="divide-y divide-gray-200" data-id="element-1541">
            <li className="px-4 py-4 sm:px-6" data-id="element-1542">
              <div className="flex items-center justify-between" data-id="element-1543">
                <p className="text-sm font-medium text-indigo-600 truncate" data-id="element-1544">
                  Auto-match process run
                </p>
                <div className="ml-2 flex-shrink-0 flex" data-id="element-1545">
                  <p className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800" data-id="element-1546">
                    System
                  </p>
                </div>
              </div>
              <div className="mt-2 sm:flex sm:justify-between" data-id="element-1547">
                <div className="sm:flex" data-id="element-1548">
                  <p className="flex items-center text-sm text-gray-500" data-id="element-1549">
                    Matched 3 invoices automatically
                  </p>
                </div>
                <div className="mt-2 flex items-center text-sm text-gray-500 sm:mt-0" data-id="element-1550">
                  <p data-id="element-1551">Today at 10:30 AM</p>
                </div>
              </div>
            </li>
            <li className="px-4 py-4 sm:px-6" data-id="element-1552">
              <div className="flex items-center justify-between" data-id="element-1553">
                <p className="text-sm font-medium text-indigo-600 truncate" data-id="element-1554">
                  Statement uploaded
                </p>
                <div className="ml-2 flex-shrink-0 flex" data-id="element-1555">
                  <p className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-blue-100 text-blue-800" data-id="element-1556">
                    Admin
                  </p>
                </div>
              </div>
              <div className="mt-2 sm:flex sm:justify-between" data-id="element-1557">
                <div className="sm:flex" data-id="element-1558">
                  <p className="flex items-center text-sm text-gray-500" data-id="element-1559">
                    Uploaded vendor statement with 10 items
                  </p>
                </div>
                <div className="mt-2 flex items-center text-sm text-gray-500 sm:mt-0" data-id="element-1560">
                  <p data-id="element-1561">Today at 10:15 AM</p>
                </div>
              </div>
            </li>
          </ul>
        </div>
      </div>
    </div>;
}